import React, { useCallback, useEffect, useMemo } from "react";
import { useThree } from "@react-three/fiber";

import {
  createGaussianTextures,
  disposeGaussianTextures,
} from "./rendering/GaussianTextures";

import {
  createGaussianBatches,
  disposeGaussianBatches,
} from "./rendering/GaussianBatches";

import {
  createDepthSortBuffers,
  sortGaussiansByDepth,
} from "./rendering/GaussianDepthSorter";

import {
  createGaussianMaterial,
  updateGaussianMaterial,
  updateGaussianViewport,
  disposeGaussianMaterial,
} from "./rendering/GaussianMaterial";

/**
 * Renders Gaussian splats as instanced quads backed by shared
 * attribute textures and globally depth-sorted instance indices.
 */
export default function GaussianSplats({
  data,
  splatScale = 1,
  opacity = 1,
  alphaCutoff = 0.01,
  controlsRef,
}) {
  const { camera, gl } = useThree();
  const renderCount = data?.count ?? 0;

  const gaussianTextures = useMemo(() => {
    if (!data || renderCount === 0) {
      return null;
    }

    return createGaussianTextures(
      data,
      renderCount,
      gl.capabilities.maxTextureSize,
    );
  }, [data, renderCount, gl]);

  const batches = useMemo(() => {
    if (!data || renderCount === 0) {
      return [];
    }

    return createGaussianBatches(renderCount);
  }, [data, renderCount]);

  const material = useMemo(
    () => createGaussianMaterial(gaussianTextures),
    [gaussianTextures],
  );

  const sortBuffers = useMemo(() => {
    if (!data || renderCount === 0) {
      return null;
    }

    return createDepthSortBuffers(renderCount);
  }, [data, renderCount]);

  const sortGaussians = useCallback(() => {
    if (!data || !sortBuffers || renderCount === 0 || batches.length === 0) {
      return;
    }

    sortGaussiansByDepth({
      positions: data.positions,
      count: renderCount,
      camera,
      batches,
      buffers: sortBuffers,
    });
  }, [data, renderCount, camera, batches, sortBuffers]);

  // Establish the initial back-to-front ordering before interaction.
  useEffect(() => {
    if (batches.length > 0) {
      sortGaussians();
    }
  }, [batches, sortGaussians]);

  /*
   * Transparent splats must be re-sorted when the camera moves.
   * Sorting on OrbitControls "end" avoids performing a large CPU
   * sort every animation frame while the user is orbiting.
   */
  useEffect(() => {
    const controls = controlsRef?.current;

    if (!controls) {
      return undefined;
    }

    controls.addEventListener("end", sortGaussians);

    return () => {
      controls.removeEventListener("end", sortGaussians);
    };
  }, [controlsRef, sortGaussians]);

  useEffect(() => {
    if (material) {
      updateGaussianViewport(material, gl);
    }
  }, [material, gl]);

  useEffect(() => {
    updateGaussianMaterial(material, {
      splatScale,
      opacity,
      alphaCutoff,
    });
  }, [material, splatScale, opacity, alphaCutoff]);

  // Three.js does not own resources created outside the JSX tree.
  useEffect(() => {
    return () => {
      disposeGaussianBatches(batches);
    };
  }, [batches]);

  useEffect(() => {
    return () => {
      disposeGaussianMaterial(material);
    };
  }, [material]);

  useEffect(() => {
    return () => {
      disposeGaussianTextures(gaussianTextures);
    };
  }, [gaussianTextures]);

  if (batches.length === 0 || !material) {
    return null;
  }

  return (
    <group>
      {batches.map((batch) => (
        <mesh
          key={batch.start}
          geometry={batch.geometry}
          material={material}
          frustumCulled={false}
          renderOrder={batch.start}
        />
      ))}
    </group>
  );
}
